// TrackComposition — first-pass structural-composition module for the Track
// Detail Panel. Renders a 5-row summary card (one row per dimension) with
// click-to-expand inline horizontal-bar charts. Reads `track.trackLevelCounts`
// directly. Defensive: returns null when no dimension has usable data.

import React, { useMemo, useState } from "react";

const DIMENSIONS = [
    { key: "companies",    label: "Companies",     singular: "company",      plural: "companies" },
    { key: "companyTypes", label: "Company types", singular: "company type", plural: "company types" },
    { key: "assetClasses", label: "Asset classes", singular: "asset class",  plural: "asset classes" },
    { key: "blockchains",  label: "Blockchains",   singular: "blockchain",   plural: "blockchains" },
    { key: "locations",    label: "Locations",     singular: "location",     plural: "locations" },
];

const _arr = (v) => (Array.isArray(v) ? v : []);

const _normalised = (entry) => {
    if (!entry || typeof entry !== "object") return null;
    const name = typeof entry.name === "string" ? entry.name.trim() : null;
    if (!name) return null;
    const count = typeof entry.count === "number" && Number.isFinite(entry.count) ? entry.count : 0;
    return { name, count };
};

const cleanList = (raw) => _arr(raw).map(_normalised).filter(Boolean);

const pluralise = (n, dim) => `${n} unique ${n === 1 ? dim.singular : dim.plural}`;

const hasUsableData = (payload) => {
    if (!payload || typeof payload !== "object") return false;
    if (cleanList(payload.top).length > 0) return true;
    if (cleanList(payload.aggregate).length > 0) return true;
    return typeof payload.uniqueBreadth === "number" && payload.uniqueBreadth > 0;
};

const breadthCountFor = (payload, top, aggregate) => {
    if (typeof payload?.uniqueBreadth === "number" && payload.uniqueBreadth > 0) return payload.uniqueBreadth;
    return aggregate.length || top.length;
};

const TopChips = ({ items }) => {
    if (!items.length) return null;
    return (
        <div className="flex flex-wrap items-center gap-1 justify-end">
            {items.map((it, i) => (
                <span
                    key={`${it.name}-${i}`}
                    title={it.name}
                    className="inline-block px-2 py-0.5 rounded border border-[hsl(var(--line))] bg-[hsl(var(--surface-sunken))] text-[10px] text-[hsl(var(--ink-soft))] truncate max-w-[140px]"
                >
                    {it.name}
                </span>
            ))}
        </div>
    );
};

const BarRow = ({ item, max, testId }) => {
    const pct = max > 0 ? Math.max(3, Math.round((item.count / max) * 100)) : 0;
    return (
        <li className="flex items-center gap-3 text-xs" data-testid={testId}>
            <span
                className="text-[hsl(var(--ink))] w-[170px] flex-shrink-0 truncate"
                title={item.name}
            >
                {item.name}
            </span>
            <div className="flex-1 h-2 rounded-sm bg-[hsl(var(--surface-sunken))] overflow-hidden">
                <div
                    aria-hidden
                    className="h-full bg-[hsl(var(--accent))]/70 rounded-sm"
                    style={{ width: `${pct}%` }}
                />
            </div>
            <span className="num text-[11px] text-[hsl(var(--ink-soft))] w-10 text-right">{item.count}</span>
        </li>
    );
};

const ExpandedDimension = ({ dim, payload }) => {
    const [showAll, setShowAll] = useState(false);

    const top = cleanList(payload?.top);
    const aggregate = cleanList(payload?.aggregate);

    // Primary list for the chart: `top` if present, otherwise the first 10
    // entries of `aggregate`.
    const chartSource = top.length > 0 ? top : aggregate.slice(0, 10);
    const head = chartSource.slice(0, 10);

    // Full list for "Show all": prefer `aggregate`, fall back to `top`.
    const fullList = aggregate.length > 0 ? aggregate : top;
    const view = showAll ? fullList : head;

    const max = view.reduce((m, x) => (x.count > m ? x.count : m), 0);

    const breadthN = breadthCountFor(payload, top, aggregate);

    if (view.length === 0) {
        return (
            <p className="text-xs text-[hsl(var(--mute))] italic mt-2">No detailed data available for this dimension.</p>
        );
    }

    return (
        <div className="mt-3 space-y-2" data-testid={`track-composition-expanded-${dim.key}`}>
            <p className="text-[11px] text-[hsl(var(--mute))]">
                {dim.label} · {pluralise(breadthN, dim)} observed across this track
            </p>
            <ul className="space-y-1.5">
                {view.map((item, i) => (
                    <BarRow
                        key={`${item.name}-${i}`}
                        item={item}
                        max={max}
                        testId={`track-composition-bar-${dim.key}-${i}`}
                    />
                ))}
            </ul>
            {fullList.length > 10 && (
                <div className="flex flex-wrap items-center gap-2 text-[11px]">
                    <button
                        type="button"
                        onClick={() => setShowAll((v) => !v)}
                        className="text-[hsl(var(--accent-ink))] hover:underline font-medium"
                        data-testid={`track-composition-toggle-${dim.key}`}
                    >
                        {showAll ? "Show top 10" : `Show all ${fullList.length}`}
                    </button>
                </div>
            )}
        </div>
    );
};

const SummaryRow = ({ dim, payload, open, onToggle, isFirst }) => {
    const top = cleanList(payload?.top);
    const aggregate = cleanList(payload?.aggregate);
    const previewSource = top.length > 0 ? top : aggregate;
    const preview = previewSource.slice(0, 3);
    const breadthN = breadthCountFor(payload, top, aggregate);

    return (
        <li className={`${isFirst ? "" : "border-t border-[hsl(var(--line))]"} ${open ? "bg-[hsl(var(--accent-soft))]/40" : ""}`}>
            <button
                type="button"
                onClick={onToggle}
                aria-expanded={open}
                data-testid={`track-composition-row-${dim.key}`}
                className={`w-full flex items-center gap-3 px-3 py-2.5 text-left hover:bg-[hsl(var(--surface-sunken))]/60 border-l-2 ${open ? "border-[hsl(var(--accent))]" : "border-transparent"}`}
            >
                <span className="text-xs font-medium text-[hsl(var(--ink))] w-[110px] flex-shrink-0">{dim.label}</span>
                <span className="text-[11px] num text-[hsl(var(--ink-soft))] flex-shrink-0 whitespace-nowrap">
                    {pluralise(breadthN, dim)}
                </span>
                <div className="flex-1 min-w-0">
                    <TopChips items={preview} />
                </div>
                <span
                    aria-hidden
                    className={`text-xs text-[hsl(var(--mute))] transition-transform inline-block ${open ? "rotate-90" : ""}`}
                >
                    ›
                </span>
            </button>
            {open && (
                <div className="px-3 pb-3 border-l-2 border-[hsl(var(--accent))]" data-testid={`track-composition-detail-${dim.key}`}>
                    <ExpandedDimension dim={dim} payload={payload} />
                </div>
            )}
        </li>
    );
};

const TrackComposition = ({ counts, defaultOpen = true }) => {
    const [sectionOpen, setSectionOpen] = useState(defaultOpen);
    const [openKey, setOpenKey] = useState(null);

    const rows = useMemo(() => {
        if (!counts || typeof counts !== "object") return [];
        return DIMENSIONS
            .map((dim) => ({ dim, payload: counts[dim.key] }))
            .filter((row) => hasUsableData(row.payload));
    }, [counts]);

    if (rows.length === 0) return null;

    return (
        <section
            className="border-t border-[hsl(var(--line))] pt-4 mt-4"
            data-testid="section-track-composition"
        >
            <button
                type="button"
                onClick={() => setSectionOpen((v) => !v)}
                className="w-full text-left flex items-center justify-between gap-3 group"
                aria-expanded={sectionOpen}
            >
                <div>
                    <div className="eyebrow">Structural composition</div>
                    <div className="text-sm font-semibold text-[hsl(var(--ink))] mt-0.5 leading-snug">Track composition</div>
                </div>
                <span className="text-xs text-[hsl(var(--mute))] group-hover:text-[hsl(var(--ink))]">{sectionOpen ? "−" : "+"}</span>
            </button>
            {sectionOpen && (
                <div className="mt-3">
                    <p className="text-[11px] text-[hsl(var(--mute))] mb-3 leading-relaxed" data-testid="track-composition-helper">
                        Most frequently observed companies, asset classes, chains and locations across the full track history.
                    </p>
                    <div
                        className="border border-[hsl(var(--line))] rounded-md overflow-hidden bg-[hsl(var(--surface-raised))]"
                        data-testid="track-composition"
                    >
                        <ul>
                            {rows.map(({ dim, payload }, i) => (
                                <SummaryRow
                                    key={dim.key}
                                    dim={dim}
                                    payload={payload}
                                    open={openKey === dim.key}
                                    isFirst={i === 0}
                                    onToggle={() => setOpenKey((k) => (k === dim.key ? null : dim.key))}
                                />
                            ))}
                        </ul>
                    </div>
                </div>
            )}
        </section>
    );
};

// Exposed for tests / future composition.
export { hasUsableData, cleanList, pluralise, DIMENSIONS };

export default TrackComposition;
